import React, { useState, useEffect } from "react";
import sentIcon from "assets/sent.svg";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import get from "lodash/get";
import { PaymentDropdown } from "components/common";

import { track } from "../../../utils/analytics";

const uuidv4 = require("uuid/v4");

const formattedDate = () => {
  const d = new Date(Date.now());
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const submitPayment = async (
  user,
  paymentMethod,
  amount,
  idempotencyKey,
  processPayment,
  setErrorMessage,
) => {
  // process Payment
  track("DASHBOARD_PAYMENT_SUBMITTED");
  processPayment({
    variables: {
      userId: user.email,
      payment_method: paymentMethod,
      amount: parseFloat(amount),
      idempotencyKey,
    },
    refetchQueries: ["User"],
    awaitRefetchQueries: true,
    update: (cache) => {
      cache.writeData({
        data: {
          user: {
            credit: {
              credit_used: user.credit.credit_used - parseFloat(amount),
            },
          },
        },
      });
    },
    onCompleted: (data) => {},
  })
    .then((res) => {
      if (
        get(res, "data.processPayment.status") &&
        // eslint-disable-next-line
        res.data.processPayment.status.toLowerCase() == "ok"
      ) {
        console.log("COMPLETED");
        track("DASHBOARD_PAYMENT_SUCCESS");
        return { status: "OK" };
      }
      throw new Error("Error processing your payment");
    })
    .catch((e) => {
      console.log(e.message);
      if (setErrorMessage) {
        setErrorMessage("Payment Failed");
      } else {
        console.error("Payment Failed - ", e);
      }
      track("DASHBOARD_PAYMENT_FAILURE", {
        message: e,
      });
      return { status: "ERROR", message: e.message };
    });
  return { status: "OK" };
};
const handleError = (err) => {
  // Handle Payment console.error();
  console.error(err);
};

const successPage = ({ finalAmount, closeDrawer }) => (
  <div className="flex flex-col p-3 text-center">
    <div className="w-full mb-4 text-xl font-bold text-gray-primary">
      Thank You!
    </div>
    <div className="w-full mb-6 text-2xl">Payment Processing</div>
    <img src={sentIcon} alt="sent" className="mb-6 m-auto" />
    <div className="w-full mb-6 flex justify-center">
      <p className="font-bold mr-1">Payment Amount:</p>
      <p>{`$${finalAmount}`}</p>
    </div>
    <button
      type="button"
      className="w-full p-5 mb-6 bg-black text-white text-2xl font-bold"
      onClick={closeDrawer}
    >
      Close
    </button>
    <div className="w-full mb-6 flex justify-center">
      Your payment should settle in the next 3 business days
    </div>
  </div>
);

const paymentPage = ({
  user,
  finalAmount,
  idempotencyKey,
  setErrorMessage,
  processPayment,
  paymentMethod,
  setPaymentMethod,
  setFinalAmount,
  setIsDone,
}) => {
  const getMethodDetails = (id) =>
    // eslint-disable-next-line
    user.payment_methods.filter((m) => m.id == id)[0];
  return (
    <div className="flex flex-col p-3 text-center">
      <div className="w-full mb-6 text-2xl">Make a payment</div>
      <div className="w-full font-bold">Payment amount:</div>
      <div className="bg-white border border-gray-400 flex mb-4 mt-2 px-3 rounded text-2xl">
        <span className="text-gray-500 border-r mr-2 pr-2 text-gray-500">
          $
        </span>
        <input
          type="text"
          className="max-w-full outline-none"
          value={finalAmount}
          onChange={(e) => {
            setFinalAmount(e.target.value);
          }}
        />
      </div>
      <div className="w-full font-bold">Payment method:</div>
      <div className="w-full mb-5 mt-3">
        {/*
          renderSelect(user, setPaymentMethod)
        */}
        <PaymentDropdown
          paymentMethods={user.payment_methods}
          defaultElementId={user.default_payment_method_id || null}
          didSelect={(p) => {
            console.log("Make a payment overlay got didSelect:", p);
            setPaymentMethod(p.id);
          }}
        />
      </div>
      <button
        type="button"
        className="w-full p-5 bg-green-primary text-white text-2xl font-bold shadow-lg"
        onClick={(e) => {
          // get paymentMethod
          submitPayment(
            user,
            paymentMethod,
            finalAmount,
            idempotencyKey,
            processPayment,
            setErrorMessage,
          )
            .then((res) => {
              setIsDone(true);
              // closeDrawer();
              // setErrorMessage('MISTAKE')
            })
            .catch((err) => {
              setErrorMessage(err.message);
              handleError(err);
            });
        }}
      >
        Confirm
      </button>
      <div className="text-xs text-gray-400 text-justify pt-4">
        {console.log("using paymentMethod", paymentMethod)}
        {paymentMethod && (
          <div>
            {`By clicking “Confirm” you are authorizing us to initiate a debit on your behalf, from your ${
              getMethodDetails(paymentMethod).institution
            } Account (...${
              getMethodDetails(paymentMethod).account_number_last_four
            }) for $${finalAmount} on ${formattedDate()}. You agree that you will not dispute this transaction, so long as the transaction corresponds to the terms of this authorization.`}
          </div>
        )}
      </div>
    </div>
  );
};

const errorPage = ({ errorMessage, setErrorMessage }) => (
  <div className="flex flex-col p-3 text-center">
    <div className="w-full mb-10 text-2xl">Payment Error</div>
    <div className="w-full mb-10 text-2xl">Oops...</div>
    <div className="w-full">
      Please check to make sure you have enough funds in your account and try
      again. If the issue persists, please contact us .
    </div>
    <button
      type="button"
      className="w-full p-5 bg-black text-white text-2xl font-bold"
      onClick={(e) => setErrorMessage(null)}
    >
      Try Again
    </button>
  </div>
);

const MakeAPayment = ({ user, closeDrawer, amount, showAmountSelectors }) => {
  console.log("user", user);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDone, setIsDone] = useState(false);
  const [idempotencyKey] = useState(uuidv4());
  const [paymentMethod, setPaymentMethod] = useState(
    user.payment_methods[0] ? user.payment_methods[0].id : null,
  );
  const [finalAmount, setFinalAmount] = useState(amount);
  const [processPayment] = useMutation(PROCESS_PAYMENT);
  console.log("final amount", finalAmount);

  useEffect(() => {
    track("DASHBOARD_OPENED_MAKE_PAYMENT_SCREEN");
  }, []);

  return (
    <div className="flex flex-col p-4 text-center sm:p-10">
      {errorMessage
        ? errorPage({ errorMessage, setErrorMessage })
        : isDone
          ? successPage({ finalAmount, closeDrawer })
          : paymentPage({
            user,
            finalAmount,
            idempotencyKey,
            closeDrawer,
            errorMessage,
            setErrorMessage,
            processPayment,
            paymentMethod,
            setPaymentMethod,
            setFinalAmount,
            showAmountSelectors,
            setIsDone,
          })}
    </div>
  );
};

const PROCESS_PAYMENT = gql`
  mutation ProcessPayment(
    $payment_method: String!
    $amount: Float!
    $idempotencyKey: String!
  ) {
    processPayment(
      payment_method: $payment_method
      amount: $amount
      idempotencyKey: $idempotencyKey
    ) {
      status
      code
      message
    }
  }
`;

// export default connect(mapStateToProps)(withDrawer(MakeAPayment));
export default MakeAPayment;
